import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';

import { AI_AGENT_HISTORY_TYPES, modelHistoryTypeOptions } from '../../../../../../../shared/constants/typeOptions';
import { cleanParams } from '../../../../../../../shared/utils/cleanParams';
import { getDateRangeValidationError } from '../../../../../../../shared/utils/time';

import type { IGetAiAgentModelSettingsHistoryParams } from '../../../../../../../shared/apis/ai-agent/model/types';
import type { AiAgentModelHistoryMgptUseYn, IAiAgentModelHistoryFilterForm } from '../types';

const initialFilterForm: IAiAgentModelHistoryFilterForm = {
  startDate: null,
  endDate: null,
  type: AI_AGENT_HISTORY_TYPES.ALL,
  mgptUseYn: 'all',
  keyword: '',
};

const parseDateParam = (value: string): Date | null => {
  if (!value) {
    return null;
  }

  return new Date(`${value}Z`);
};

const formatDateParam = (date: Date | null, isEndOfDay = false): string => {
  if (!date) {
    return '';
  }

  return (isEndOfDay ? dayjs(date).endOf('day') : dayjs(date).startOf('day')).toISOString().substring(0, 19);
};

const isAiAgentModelHistoryMgptUseYn = (value: string): value is AiAgentModelHistoryMgptUseYn => {
  return ['all', 'Y', 'N'].includes(value);
};

const parsePositiveInteger = (value: string | null, fallback: number): number => {
  const parsedValue = Number(value);
  return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : fallback;
};

export default function useAiAgentModelHistoryFilters(companyId: number, modelId: number) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterForm, setFilterForm] = useState<IAiAgentModelHistoryFilterForm>(initialFilterForm);

  const startDateParam = searchParams.get('modStartDt') || searchParams.get('startDate') || '';
  const endDateParam = searchParams.get('modEndDt') || searchParams.get('endDate') || '';
  const typeParam = searchParams.get('type') || AI_AGENT_HISTORY_TYPES.ALL;
  const mgptUseYnParam = searchParams.get('mgptUseYn') || 'all';
  const keywordParam = searchParams.get('keyword') || '';

  const queryParams: IGetAiAgentModelSettingsHistoryParams = {
    companyId,
    modelId,
    startDate: startDateParam || undefined,
    endDate: endDateParam || undefined,
    changeType: typeParam === AI_AGENT_HISTORY_TYPES.ALL ? undefined : typeParam,
    useYn: mgptUseYnParam === 'Y' || mgptUseYnParam === 'N' ? mgptUseYnParam : undefined,
    manager: keywordParam || undefined,
    page: parsePositiveInteger(searchParams.get('page'), 1),
    size: parsePositiveInteger(searchParams.get('size'), 10),
  };

  const updateFilterForm = (nextFilter: Partial<IAiAgentModelHistoryFilterForm>) => {
    setFilterForm((prev) => ({
      ...prev,
      ...nextFilter,
    }));
  };

  const applyFilters = () => {
    const dateRangeValidationError = getDateRangeValidationError(filterForm.startDate, filterForm.endDate);

    if (dateRangeValidationError) {
      void Swal.fire({
        icon: 'error',
        ...dateRangeValidationError,
        confirmButtonText: '확인',
      });

      return;
    }

    const currentParams = Object.fromEntries(searchParams.entries());

    setSearchParams(
      cleanParams({
        ...currentParams,
        page: '1',
        startDate: '',
        endDate: '',
        modStartDt: formatDateParam(filterForm.startDate),
        modEndDt: formatDateParam(filterForm.endDate, true),
        type: filterForm.type === AI_AGENT_HISTORY_TYPES.ALL ? '' : filterForm.type,
        mgptUseYn: filterForm.mgptUseYn === 'all' ? '' : filterForm.mgptUseYn,
        keyword: filterForm.keyword.trim(),
      }),
    );
  };

  const resetFilters = () => {
    setFilterForm(initialFilterForm);

    const currentParams = Object.fromEntries(searchParams.entries());

    setSearchParams(
      cleanParams({
        ...currentParams,
        page: '1',
        startDate: '',
        endDate: '',
        modStartDt: '',
        modEndDt: '',
        type: '',
        mgptUseYn: '',
        keyword: '',
      }),
    );
  };

  const handlePageChange = (page: number) => {
    const currentParams = Object.fromEntries(searchParams.entries());

    setSearchParams(
      cleanParams({
        ...currentParams,
        page: String(page),
      }),
    );
  };

  useEffect(() => {
    setFilterForm({
      startDate: parseDateParam(startDateParam),
      endDate: parseDateParam(endDateParam),
      type: modelHistoryTypeOptions.find((option) => option.value === typeParam)?.value || AI_AGENT_HISTORY_TYPES.ALL,
      mgptUseYn: isAiAgentModelHistoryMgptUseYn(mgptUseYnParam) ? mgptUseYnParam : 'all',
      keyword: keywordParam,
    });
  }, [endDateParam, keywordParam, mgptUseYnParam, startDateParam, typeParam]);

  return {
    filterForm,
    queryParams,
    updateFilterForm,
    applyFilters,
    resetFilters,
    handlePageChange,
  };
}
